/**
 * LoansPage — operator surface labelled "Financing" in the navigation.
 *
 * The route stays /loans (the underlying domain aggregate is still
 * `loan_id`, and every bookmark/URL/audit row points there) but the
 * sidebar + page title use the product-facing name "Financing"
 * because that's what dealers + loan officers call the offering on
 * the floor.
 *
 * What the page does:
 *   - Lists all loans in the caller's country, dealer-scoped on the
 *     server side via `loan.read`.
 *   - Joins each loan against its customer + device + dealer
 *     client-side (separate list fetches; small N at MVP volume).
 *   - Filter bar across the top: status[], dealer dropdown, risk
 *     class[], date range, lock stage range. All AND-ed.
 *   - Search box matches: customer given/family name, phone, IMEI,
 *     device brand/model, dealer name, and the loan/customer/device
 *     id prefixes. Case-insensitive substring.
 *   - Each row's Customer / Device / Dealer cell is a Link to that
 *     entity's detail page so the operator can pivot in one tap.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import type { Customer, Dealer, Device, Loan, LoanStatus } from "@dfp/api-client";
import { Badge, type BadgeTone } from "../components/Badge.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CollapsibleCard } from "../components/CollapsibleCard.js";
import { CreateLoanForm } from "../components/CreateLoanForm.js";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

const STATUS_TONE: Record<LoanStatus, BadgeTone> = {
  DRAFT: "neutral",
  PENDING_VERIFICATION: "warning",
  PENDING_DEPOSIT: "warning",
  // Money in, waiting on DPC to report Device Owner + restrictions
  // applied + snapshot fresh. Operationally the dealer's next move is
  // "go install the DPC on the device".
  PENDING_DEVICE_PROVISIONING: "warning",
  ACTIVE: "success",
  IN_ARREARS: "warning",
  LOCKED: "danger",
  IN_DISPUTE: "warning",
  PENDING_RELEASE: "brand",
  PAID_OFF: "success",
  WRITTEN_OFF: "danger",
  // Declined at verification — never financed, so it's a neutral outcome, not
  // a loss (WRITTEN_OFF = danger).
  REJECTED: "neutral",
};

const STATUS_CHOICES: readonly LoanStatus[] = [
  "DRAFT",
  "PENDING_VERIFICATION",
  "PENDING_DEPOSIT",
  "PENDING_DEVICE_PROVISIONING",
  "ACTIVE",
  "IN_ARREARS",
  "LOCKED",
  "IN_DISPUTE",
  "PENDING_RELEASE",
  "PAID_OFF",
  "WRITTEN_OFF",
  "REJECTED",
];

const RISK_CHOICES = ["A", "B", "C"] as const;

export function LoansPage(): JSX.Element {
  // Drill-down deep-links (dashboard status cards, customer "loans"
  // pivot) arrive as ?status= / ?customerId=. strict:false keeps this
  // page usable from any nav path without the route's typed search api.
  const urlSearch = useSearch({ strict: false }) as {
    status?: string;
    customerId?: string;
  };
  const initialStatus = urlSearch.status;
  const customerFilter = urlSearch.customerId;

  const loans = useQuery({
    queryKey: ["loans", "list"],
    queryFn: () => api.get<readonly Loan[]>("/api/loans"),
  });
  // Side-fetches for the join. Each returns the country-scoped list;
  // for MVP-scale fleets this is a fixed-size lookup, so the cost is
  // bounded. When the lists grow past a few thousand rows we move the
  // join to a server-side /api/loans?include=customer,device endpoint.
  const customers = useQuery({
    queryKey: ["customers", "list"],
    queryFn: () => api.get<readonly Customer[]>("/api/customers"),
  });
  const devices = useQuery({
    queryKey: ["devices", "list"],
    queryFn: () => api.get<readonly Device[]>("/api/devices"),
  });
  const dealers = useQuery({
    queryKey: ["dealers", "list"],
    queryFn: () => api.get<readonly Dealer[]>("/api/dealers"),
  });

  const customerById = useMemo(
    () => new Map((customers.data ?? []).map((c) => [c.id, c])),
    [customers.data],
  );
  const deviceById = useMemo(
    () => new Map((devices.data ?? []).map((d) => [d.id, d])),
    [devices.data],
  );
  const dealerById = useMemo(
    () => new Map((dealers.data ?? []).map((d) => [d.id, d])),
    [dealers.data],
  );

  /* ────── Filter + search state ────── */
  // Seed the status filter from the URL drill-down (?status=ACTIVE) so a
  // dashboard card lands here pre-filtered. Validated against the known
  // status union; an unknown value just yields no seed.
  const [statuses, setStatuses] = useState<readonly LoanStatus[]>(() =>
    initialStatus && (STATUS_CHOICES as readonly string[]).includes(initialStatus)
      ? [initialStatus as LoanStatus]
      : [],
  );
  const [dealerId, setDealerId] = useState<string>("");
  const [riskClasses, setRiskClasses] = useState<readonly string[]>([]);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  // Brand/model moved to multi-select. Derive available options
  // from the devices list — but for models, gate the list to the
  // selected brands so picking "Samsung" doesn't surface Tecno models.
  const [brands, setBrands] = useState<readonly string[]>([]);
  const [models, setModels] = useState<readonly string[]>([]);

  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    for (const d of devices.data ?? []) set.add(d.brand);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [devices.data]);
  const modelOptions = useMemo(() => {
    const set = new Set<string>();
    for (const d of devices.data ?? []) {
      // When brands are selected we only show models that belong
      // to those brands. With no brand filter, every model is
      // available — the user can still filter by model alone.
      if (brands.length === 0 || brands.includes(d.brand)) {
        set.add(d.model);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [devices.data, brands]);

  // If the user deselects a brand, drop any models that no longer
  // belong to the remaining brand set so a stale model filter doesn't
  // produce a confusing empty result.
  useEffect(() => {
    if (brands.length === 0) return;
    const allowed = new Set<string>();
    for (const d of devices.data ?? []) {
      if (brands.includes(d.brand)) allowed.add(d.model);
    }
    const next = models.filter((m) => allowed.has(m));
    if (next.length !== models.length) setModels(next);
  }, [brands, devices.data, models]);

  const filtered = useMemo(() => {
    const all = loans.data ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((loan) => {
      // Customer deep-link (?customerId=) — narrows to one customer's loans.
      if (customerFilter && loan.customerId !== customerFilter) return false;
      // Status filter — empty = no narrowing.
      if (statuses.length > 0 && !statuses.includes(loan.status)) return false;
      // Dealer dropdown.
      if (dealerId && loan.dealerId !== dealerId) return false;
      // Risk class via the joined device.
      if (riskClasses.length > 0) {
        const device = deviceById.get(loan.deviceId);
        if (!device || !riskClasses.includes(device.modelRiskClass)) return false;
      }
      // Brand multi-select via the joined device.
      if (brands.length > 0) {
        const device = deviceById.get(loan.deviceId);
        if (!device || !brands.includes(device.brand)) return false;
      }
      // Model multi-select.
      if (models.length > 0) {
        const device = deviceById.get(loan.deviceId);
        if (!device || !models.includes(device.model)) return false;
      }
      // Date range — inclusive on both ends. Empty = no narrowing.
      const created = new Date(loan.createdAt).valueOf();
      if (startDate) {
        const start = new Date(startDate).valueOf();
        if (created < start) return false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (created > end.valueOf()) return false;
      }
      // Free-text search.
      if (q.length > 0) {
        const customer = customerById.get(loan.customerId);
        const device = deviceById.get(loan.deviceId);
        const dealer = dealerById.get(loan.dealerId);
        const haystack = [
          loan.id,
          customer?.givenName,
          customer?.familyName,
          customer?.phoneMasked,
          device?.brand,
          device?.model,
          device?.imei,
          dealer?.name,
        ]
          .filter((v): v is string => typeof v === "string")
          .map((v) => v.toLowerCase());
        if (!haystack.some((s) => s.includes(q))) return false;
      }
      return true;
    });
  }, [
    loans.data,
    statuses,
    dealerId,
    riskClasses,
    brands,
    models,
    startDate,
    endDate,
    search,
    customerFilter,
    customerById,
    deviceById,
    dealerById,
  ]);

  function toggle<T>(value: T, list: readonly T[], setter: (next: readonly T[]) => void): void {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  function clearFilters(): void {
    setStatuses([]);
    setDealerId("");
    setRiskClasses([]);
    setBrands([]);
    setModels([]);
    setStartDate("");
    setEndDate("");
    setSearch("");
  }

  const anyFilterActive =
    statuses.length > 0 ||
    dealerId.length > 0 ||
    riskClasses.length > 0 ||
    brands.length > 0 ||
    models.length > 0 ||
    startDate.length > 0 ||
    endDate.length > 0 ||
    search.trim().length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-simuflex-navy">Financing</h1>
        <p className="text-sm text-simuflex-slate">
          Every customer financing arrangement — loan + customer + device + dealer joined in one
          view. Use filters or search to narrow.
        </p>
      </div>

      <CreateLoanForm />

      {/*
        Filter + search — collapsed by default since most operators
        want the table front-and-centre. The dot in the header lights
        green when any filter is active so the collapsed state still
        signals "this list is narrowed". Free-text search is also
        inside the collapsible to keep the layout consistent.
      */}
      <CollapsibleCard
        title="Filters & search"
        defaultOpen={false}
        hasActiveContent={anyFilterActive}
        headerAction={
          anyFilterActive ? (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[11px] font-bold uppercase tracking-wider text-simuflex-slate hover:text-simuflex-navy"
            >
              Clear all
            </button>
          ) : null
        }
      >
        <div className="space-y-4 text-xs">
          {/* Search */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
              Search
            </label>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Customer name, phone, IMEI, brand/model, dealer, loan id…"
              className="mt-1 block w-full rounded-lg border border-simuflex-line bg-white px-3 py-2 text-simuflex-navy"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Dealer */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
                Dealer
              </label>
              <select
                value={dealerId}
                onChange={(e) => setDealerId(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-simuflex-line bg-white px-2 py-1.5 text-simuflex-navy"
              >
                <option value="">All dealers</option>
                {(dealers.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Brand multi-select */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
                Brand
              </label>
              <div className="mt-1">
                <MultiSelectDropdown
                  label="Brand"
                  options={brandOptions}
                  selected={brands}
                  onChange={setBrands}
                  placeholder="All brands"
                />
              </div>
            </div>

            {/* Model multi-select — narrows by brand when applicable */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
                Model
              </label>
              <div className="mt-1">
                <MultiSelectDropdown
                  label="Model"
                  options={modelOptions}
                  selected={models}
                  onChange={setModels}
                  placeholder={brands.length > 0 ? "All models for these brands" : "All models"}
                />
              </div>
            </div>

            {/* Risk class */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
                Risk class
              </label>
              <div className="mt-1 flex gap-1.5">
                {RISK_CHOICES.map((c) => {
                  const active = riskClasses.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggle(c, riskClasses, setRiskClasses)}
                      className={`px-3 py-1 rounded-full text-xs font-bold border ${
                        active
                          ? "bg-simuflex-navy text-white border-simuflex-navy"
                          : "bg-white text-simuflex-navy border-simuflex-line"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date range */}
            <div>
              <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
                Created between
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="rounded-lg border border-simuflex-line bg-white px-2 py-1 text-simuflex-navy"
                />
                <span className="text-simuflex-slate">–</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="rounded-lg border border-simuflex-line bg-white px-2 py-1 text-simuflex-navy"
                />
              </div>
            </div>
          </div>

          {/* Status chips — multi-select. */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
              Status (any of)
            </label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {STATUS_CHOICES.map((s) => {
                const active = statuses.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggle(s, statuses, setStatuses)}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold border tracking-wider ${
                      active
                        ? "bg-simuflex-navy text-white border-simuflex-navy"
                        : "bg-white text-simuflex-navy border-simuflex-line"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>All financing</CardTitle>
          <span className="text-xs text-simuflex-slate">
            {filtered.length}
            {anyFilterActive ? ` of ${loans.data?.length ?? 0}` : ""} rows
          </span>
        </CardHeader>
        <CardBody className="p-0">
          {loans.isLoading ? (
            <p className="p-5 text-sm text-simuflex-slate">Loading…</p>
          ) : loans.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load financing list.</p>
          ) : filtered.length === 0 ? (
            <p className="p-5 text-sm text-simuflex-slate">
              {anyFilterActive
                ? "No financing matches the current filters."
                : "No financing arrangements yet."}
            </p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Device</Th>
                  <Th>Dealer</Th>
                  <Th>Principal</Th>
                  <Th>Status</Th>
                  <Th>Contract</Th>
                  <Th>Lock stage</Th>
                  <Th>Created</Th>
                  <Th>Loan</Th>
                </tr>
              </THead>
              <TBody>
                {filtered.map((loan) => {
                  const customer = customerById.get(loan.customerId);
                  const device = deviceById.get(loan.deviceId);
                  const dealer = dealerById.get(loan.dealerId);
                  return (
                    <Tr key={loan.id}>
                      <Td>
                        {customer ? (
                          <Link
                            to="/customers/$id"
                            params={{ id: customer.id }}
                            className="text-simuflex-navy font-semibold hover:text-simuflex-green hover:underline"
                          >
                            {customer.givenName} {customer.familyName}
                          </Link>
                        ) : (
                          <span className="font-mono text-xs text-simuflex-slate">
                            {loan.customerId.slice(0, 8)}…
                          </span>
                        )}
                        {customer?.phoneMasked ? (
                          <div className="text-[11px] text-simuflex-slate">
                            {customer.phoneMasked}
                          </div>
                        ) : null}
                      </Td>
                      <Td>
                        {device ? (
                          <Link
                            to="/devices/$id"
                            params={{ id: device.id }}
                            className="text-simuflex-navy font-semibold hover:text-simuflex-green hover:underline"
                          >
                            {device.brand} {device.model}
                          </Link>
                        ) : (
                          <span className="font-mono text-xs text-simuflex-slate">
                            {loan.deviceId.slice(0, 8)}…
                          </span>
                        )}
                        {device?.imei ? (
                          <div className="text-[11px] text-simuflex-slate font-mono">
                            IMEI {device.imei}
                          </div>
                        ) : null}
                      </Td>
                      <Td>
                        {dealer ? (
                          <Link
                            to="/dealers/$id"
                            params={{ id: dealer.id }}
                            className="text-simuflex-navy hover:text-simuflex-green hover:underline text-sm"
                          >
                            {dealer.name}
                          </Link>
                        ) : (
                          <span className="font-mono text-xs text-simuflex-slate">
                            {loan.dealerId.slice(0, 8)}…
                          </span>
                        )}
                      </Td>
                      <Td>{formatMoney(loan.principalAmountMinor, loan.principalCurrency)}</Td>
                      <Td>
                        <Badge tone={STATUS_TONE[loan.status]}>{loan.status}</Badge>
                      </Td>
                      <Td>
                        {loan.contractId ? (
                          <Link
                            to="/contracts/$id"
                            params={{ id: loan.contractId }}
                            className="text-xs text-simuflex-green hover:underline"
                          >
                            View
                          </Link>
                        ) : (
                          <span className="text-xs text-simuflex-slate">Unsigned</span>
                        )}
                      </Td>
                      <Td>{loan.lockStage}</Td>
                      <Td className="text-xs text-simuflex-slate">
                        {new Date(loan.createdAt).toLocaleDateString()}
                      </Td>
                      <Td>
                        <Link
                          to="/loans/$id"
                          params={{ id: loan.id }}
                          className="font-mono text-xs text-simuflex-green hover:underline"
                        >
                          {loan.id.slice(0, 8)}…
                        </Link>
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
